import { create } from 'zustand';
import { GameService } from '../firebase/gameService';
import { Card, CardType, GamePhase, Team } from '../types';
import { LANGUAGE_SETS } from '../utils/constants';

interface GameStore {
    // State
    cards: Card[];
    currentTeam: Team;
    gamePhase: GamePhase;
    spymasterMode: boolean;
    showFullBoard: boolean;
    redCards: number;
    blueCards: number;
    winner: Team | null;
    gameStartTime: number | null;
    elapsedTime: number;
    selectedLanguageSetId: string;
    gameId: string | null;
    isConnected: boolean;
    unsubscribe?: () => void;
    lastMove: {
        cardIndex: number;
        previousTeam: Team;
        previousRedCards: number;
        previousBlueCards: number;
        previousGamePhase: GamePhase;
        previousWinner: Team | null;
    } | null;

    // Actions
    setCards: (cards: Card[]) => void;
    setCurrentTeam: (team: Team) => void;
    setGamePhase: (phase: GamePhase) => void;
    setSpymasterMode: (mode: boolean) => void;
    setShowFullBoard: (show: boolean) => void;
    setRedCards: (count: number) => void;
    setBlueCards: (count: number) => void;
    setWinner: (winner: Team | null) => void;
    setGameStartTime: (time: number | null) => void;
    setElapsedTime: (time: number) => void;
    setSelectedLanguageSetId: (languageSetId: string) => void;
    setGameId: (gameId: string | null) => void;
    setIsConnected: (connected: boolean) => void;

    // Complex actions
    generateBoard: (updateUrl?: (gameId: string) => void) => Promise<void>;
    handleCardClick: (index: number) => Promise<void>;
    endTurn: () => Promise<void>;
    undoLastMove: () => void;
    loadBoardFromState: (words: string[], types: CardType[], languageSetId?: string) => void;
    loadGameFromFirebase: (gameId: string) => Promise<boolean>;
    subscribeToGame: (gameId: string) => void;
    unsubscribeFromGame: () => void;
    resetTimer: () => void;
}

export const useGameStore = create<GameStore>((set, get) => ({
    // Initial state
    cards: [],
    currentTeam: 'red',
    gamePhase: 'setup',
    spymasterMode: false,
    showFullBoard: false,
    redCards: 9,
    blueCards: 8,
    winner: null,
    gameStartTime: null,
    elapsedTime: 0,
    selectedLanguageSetId: 'arabic', // Default to Arabic
    gameId: null,
    isConnected: false,
    unsubscribe: undefined,
    lastMove: null,

    // Simple setters
    setCards: (cards) => set({ cards }),
    setCurrentTeam: (currentTeam) => set({ currentTeam }),
    setGamePhase: (gamePhase) => set({ gamePhase }),
    setSpymasterMode: (spymasterMode) => set({ spymasterMode }),
    setShowFullBoard: (showFullBoard) => set({ showFullBoard }),
    setRedCards: (redCards) => set({ redCards }),
    setBlueCards: (blueCards) => set({ blueCards }),
    setWinner: (winner) => set({ winner }),
    setGameStartTime: (gameStartTime) => set({ gameStartTime }),
    setElapsedTime: (elapsedTime) => set({ elapsedTime }),
    setSelectedLanguageSetId: (selectedLanguageSetId) => set({ selectedLanguageSetId }),
    setGameId: (gameId) => set({ gameId }),
    setIsConnected: (isConnected) => set({ isConnected }),

    // Complex actions
    generateBoard: async (updateUrl) => {
        const { selectedLanguageSetId } = get();
        const languageSet = LANGUAGE_SETS.find(set => set.id === selectedLanguageSetId) || LANGUAGE_SETS[0];

        // Randomly select 25 words from the selected language set
        const shuffledWords = [...languageSet.words].sort(() => Math.random() - 0.5).slice(0, 25);

        // Create card types array: 9 red, 8 blue, 7 neutral, 1 assassin
        const cardTypes: CardType[] = [
            ...Array(9).fill('red'),
            ...Array(8).fill('blue'),
            ...Array(7).fill('neutral'),
            'assassin'
        ];

        // Shuffle card types
        const shuffledTypes = cardTypes.sort(() => Math.random() - 0.5);

        // Create cards
        const newCards: Card[] = shuffledWords.map((word, index) => ({
            word,
            type: shuffledTypes[index],
            revealed: false
        }));

        try {
            // Create game in Firebase
            const gameId = await GameService.createGame(newCards, selectedLanguageSetId);

            set({
                cards: newCards,
                currentTeam: 'red',
                gamePhase: 'playing',
                redCards: 9,
                blueCards: 8,
                winner: null,
                gameStartTime: Date.now(),
                elapsedTime: 0,
                lastMove: null,
                showFullBoard: false,
                gameId,
                isConnected: true
            });

            // Update URL with game ID
            if (updateUrl) {
                updateUrl(gameId);
            }
        } catch (error) {
            console.error('Error creating game:', error);
            // Fallback to local game
            set({
                cards: newCards,
                currentTeam: 'red',
                gamePhase: 'playing',
                redCards: 9,
                blueCards: 8,
                winner: null,
                gameStartTime: Date.now(),
                elapsedTime: 0,
                lastMove: null,
                showFullBoard: false,
                gameId: 'local-' + Date.now(), // Generate a local ID
                isConnected: false
            });
        }
    },

    handleCardClick: async (index) => {
        const state = get();
        if (state.gamePhase !== 'playing' || state.cards[index].revealed) return;

        // Save current state for undo
        const lastMove = {
            cardIndex: index,
            previousTeam: state.currentTeam,
            previousRedCards: state.redCards,
            previousBlueCards: state.blueCards,
            previousGamePhase: state.gamePhase,
            previousWinner: state.winner
        };

        const newCards = [...state.cards];
        newCards[index].revealed = true;

        const cardType = newCards[index].type;
        let newCurrentTeam = state.currentTeam;
        let newRedCards = state.redCards;
        let newBlueCards = state.blueCards;
        let newGamePhase: GamePhase = state.gamePhase;
        let newWinner = state.winner;

        if (cardType === 'assassin') {
            newWinner = state.currentTeam === 'red' ? 'blue' : 'red';
            newGamePhase = 'gameOver';
        } else if (cardType === state.currentTeam) {
            const newCount = state.currentTeam === 'red' ? state.redCards - 1 : state.blueCards - 1;
            if (state.currentTeam === 'red') {
                newRedCards = newCount;
            } else {
                newBlueCards = newCount;
            }

            if (newCount === 0) {
                newWinner = state.currentTeam;
                newGamePhase = 'gameOver';
            }
            // Continue turn for correct guess
        } else {
            // Wrong team or neutral - end turn
            if (cardType === 'red' && state.currentTeam === 'blue') {
                newRedCards = state.redCards - 1;
            } else if (cardType === 'blue' && state.currentTeam === 'red') {
                newBlueCards = state.blueCards - 1;
            }
            newCurrentTeam = state.currentTeam === 'red' ? 'blue' : 'red';
        }

        // Update local state first
        set({
            cards: newCards,
            currentTeam: newCurrentTeam,
            redCards: newRedCards,
            blueCards: newBlueCards,
            gamePhase: newGamePhase,
            winner: newWinner,
            lastMove
        });

        // Sync with Firebase if connected
        if (state.gameId && state.isConnected) {
            try {
                console.log('Syncing card state with Firebase:', {
                    gameId: state.gameId,
                    cardIndex: index,
                    cardType: cardType
                });
                await GameService.updateCardState(
                    state.gameId,
                    index,
                    newCards,
                    newCurrentTeam,
                    newRedCards,
                    newBlueCards,
                    newGamePhase,
                    newWinner
                );
                console.log('Card state synced successfully');
            } catch (error) {
                console.error('Failed to sync card state with Firebase:', error);
            }
        } else {
            console.log('Not connected to Firebase, local update only');
        }
    },

    endTurn: async () => {
        const state = get();
        const newCurrentTeam = state.currentTeam === 'red' ? 'blue' : 'red';

        set({ currentTeam: newCurrentTeam, lastMove: null }); // Clear last move when ending turn

        // Sync with Firebase if connected
        if (state.gameId && state.isConnected) {
            try {
                await GameService.updateCurrentTeam(state.gameId, newCurrentTeam);
            } catch (error) {
                console.error('Failed to sync turn change with Firebase:', error);
            }
        }
    },

    undoLastMove: () => {
        const state = get();
        if (!state.lastMove) return;

        const { cardIndex, previousTeam, previousRedCards, previousBlueCards, previousGamePhase, previousWinner } = state.lastMove;

        const newCards = [...state.cards];
        newCards[cardIndex].revealed = false;

        set({
            cards: newCards,
            currentTeam: previousTeam,
            redCards: previousRedCards,
            blueCards: previousBlueCards,
            gamePhase: previousGamePhase,
            winner: previousWinner,
            lastMove: null
        });
    },

    loadBoardFromState: (words, types, languageSetId) => {
        const newCards: Card[] = words.map((word, index) => ({
            word,
            type: types[index],
            revealed: false
        }));

        set({
            cards: newCards,
            currentTeam: 'red',
            gamePhase: 'playing',
            redCards: 9,
            blueCards: 8,
            winner: null,
            gameStartTime: Date.now(),
            elapsedTime: 0,
            selectedLanguageSetId: languageSetId || 'arabic',
            lastMove: null,
            showFullBoard: false,
            gameId: null,
            isConnected: false
        });
    },

    loadGameFromFirebase: async (gameId: string): Promise<boolean> => {
        try {
            const gameData = await GameService.getGame(gameId);
            if (gameData) {
                set({
                    cards: gameData.cards,
                    currentTeam: gameData.currentTeam,
                    gamePhase: gameData.gamePhase,
                    redCards: gameData.redCards,
                    blueCards: gameData.blueCards,
                    winner: gameData.winner,
                    gameStartTime: gameData.gameStartTime.toMillis(),
                    selectedLanguageSetId: gameData.selectedLanguageSetId,
                    gameId: gameData.id,
                    isConnected: true,
                    lastMove: null,
                    showFullBoard: false
                });
                return true;
            }
            return false;
        } catch (error) {
            console.error('Error loading game from Firebase:', error);
            return false;
        }
    },

    subscribeToGame: (gameId: string) => {
        const state = get();

        // Unsubscribe from any existing subscription first
        if (state.unsubscribe) {
            state.unsubscribe();
        }

        console.log('Subscribing to game:', gameId);

        const unsubscribe = GameService.subscribeToGame(gameId, (gameData) => {
            if (gameData) {
                console.log('Received game update:', gameData);
                set({
                    cards: gameData.cards,
                    currentTeam: gameData.currentTeam,
                    gamePhase: gameData.gamePhase,
                    redCards: gameData.redCards,
                    blueCards: gameData.blueCards,
                    winner: gameData.winner,
                    gameStartTime: gameData.gameStartTime.toMillis(),
                    selectedLanguageSetId: gameData.selectedLanguageSetId,
                    gameId: gameData.id,
                    isConnected: true
                });
            } else {
                console.log('Game not found or connection lost');
                set({
                    isConnected: false,
                    gameId: null
                });
            }
        });

        // Store unsubscribe function
        set({ unsubscribe, isConnected: true });
    },

    unsubscribeFromGame: () => {
        const state = get();
        if (state.unsubscribe) {
            state.unsubscribe();
        }
        set({
            isConnected: false,
            gameId: null,
            unsubscribe: undefined
        });
    },

    resetTimer: () => {
        set({
            gameStartTime: Date.now(),
            elapsedTime: 0
        });
    }
}));
